// Canonical TS source-of-truth for the 3 built-in role permission sets.
// Mirrors the seed migration 20260505000002_bootstrap_system_roles.sql —
// CI should diff these two lists.
//
// Used as a fallback when the backend hasn't returned the member's role
// permissions yet (e.g. transient network blip after login). UI-side
// shouldn't render with empty permissions during that window.

import type { Permission } from './permissions';
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from './permissions';

const MEMBER_READ_ONLY = new Set<string>([
  'clients',
  'suppliers',
  'articles',
  'representatives',
  'stock',
  'treasury',
  'reports',
  'settings',
]);
const MEMBER_FULL = new Set<string>(['sales', 'purchases', 'payments']);

function ownerPermissions(): Permission[] {
  const out: Permission[] = [];
  for (const m of PERMISSION_MODULES) {
    for (const a of PERMISSION_ACTIONS) {
      out.push({ module: m, action: a });
    }
  }
  return out;
}

function adminPermissions(): Permission[] {
  return ownerPermissions().filter(
    (p) =>
      !(
        (p.module === 'billing' && p.action === 'delete') ||
        (p.module === 'team' && p.action === 'delete')
      ),
  );
}

function memberPermissions(): Permission[] {
  const out: Permission[] = [];
  for (const m of MEMBER_READ_ONLY) {
    out.push({ module: m, action: 'view' });
  }
  for (const m of MEMBER_FULL) {
    out.push({ module: m, action: 'view' });
    out.push({ module: m, action: 'create' });
    out.push({ module: m, action: 'edit' });
  }
  return out;
}

export const SYSTEM_ROLE_PERMISSIONS = {
  owner: ownerPermissions(),
  admin: adminPermissions(),
  member: memberPermissions(),
} as const;

export const SYSTEM_ROLE_SLUGS = ['owner', 'admin', 'member'] as const;
export type SystemRoleSlug = (typeof SYSTEM_ROLE_SLUGS)[number];

export function isSystemRoleSlug(slug: string): slug is SystemRoleSlug {
  return SYSTEM_ROLE_SLUGS.includes(slug as SystemRoleSlug);
}
